import type { AnyXYChartScale } from '../../types/xy-chart-internals.js';
import type { AreaDatapoint } from '../../types/xy-chart.js';
import { getBandCenteredPoint } from '../../utils/get-band-centered-point.js';
import { getCenterDate } from '../../utils/get-center-date.js';
import {
  isCategoricalAreaScale,
  isNumericalAreaScale,
  isTimeAreaScale,
} from '../../utils/type-guards.js';

/**
 * Creates a direct value accessor for band charts.
 * Unlike stacking accessors, band accessors return the scaled value directly
 * without any accumulation or baseline calculations.
 *
 * For full-band charts (where all x0, x1, y0, y1 are defined) with time on the x-axis,
 * the accessor centers the x value between x0 and x1 to ensure consistency with domain calculations.
 *
 * @param accessorKey - The key to access the value from the datapoint
 * @param relAccessor - Optional function to convert absolute numerical values to relative values
 */
const getBandValueAccessor = (accessorKey: 'x0' | 'x1' | 'y0' | 'y1') => {
  return (scale: AnyXYChartScale, relAccessor?: (value: number) => number) => {
    return (datapoint: AreaDatapoint) => {
      const value = datapoint[accessorKey];

      if (value === undefined) {
        throw Error(
          `${accessorKey} cannot be undefined for band chart accessor`,
        );
      }

      const args = { scale, v0: value, v1: undefined };

      if (isNumericalAreaScale(args)) {
        return args.scale(relAccessor ? relAccessor(args.v0) : args.v0);
      }

      if (isTimeAreaScale(args)) {
        const isXAccessor = accessorKey === 'x0' || accessorKey === 'x1';
        const hasAllBandCoordinates =
          datapoint.x0 !== undefined &&
          datapoint.x1 !== undefined &&
          datapoint.y0 !== undefined &&
          datapoint.y1 !== undefined;

        if (hasAllBandCoordinates && isXAccessor) {
          const centeredTimestamp = getCenterDate(
            datapoint.x0 as Date,
            datapoint.x1 as Date,
          );
          return args.scale(new Date(centeredTimestamp));
        }

        return args.scale(args.v0);
      }

      if (isCategoricalAreaScale(args)) {
        return getBandCenteredPoint(args.scale, args.v0);
      }

      throw Error(`unknown scale type for band accessor ${accessorKey}`);
    };
  };
};

/**
 * Band chart x0 accessor - returns the scaled x0 value directly.
 */
export const bandX0Accessor = (
  scale: AnyXYChartScale,
  relAccessor?: (value: number) => number,
) => getBandValueAccessor('x0')(scale, relAccessor);

/**
 * Band chart x1 accessor - returns the scaled x1 value directly.
 */
export const bandX1Accessor = (
  scale: AnyXYChartScale,
  relAccessor?: (value: number) => number,
) => getBandValueAccessor('x1')(scale, relAccessor);

/**
 * Band chart y0 accessor - returns the scaled y0 value directly.
 */
export const bandY0Accessor = (
  scale: AnyXYChartScale,
  relAccessor?: (value: number) => number,
) => getBandValueAccessor('y0')(scale, relAccessor);

/**
 * Band chart y1 accessor - returns the scaled y1 value directly.
 */
export const bandY1Accessor = (
  scale: AnyXYChartScale,
  relAccessor?: (value: number) => number,
) => getBandValueAccessor('y1')(scale, relAccessor);

export function xMiddleBandAccessor(
  scale: AnyXYChartScale,
  relAccessor?: (value: number) => number,
) {
  return (datapoint: AreaDatapoint) => {
    const x0 = bandX0Accessor(scale, relAccessor)(datapoint);
    const x1 = bandX1Accessor(scale, relAccessor)(datapoint);
    return (x0 + x1) / 2;
  };
}

export function yMiddleBandAccessor(
  scale: AnyXYChartScale,
  relAccessor?: (value: number) => number,
) {
  return (datapoint: AreaDatapoint) => {
    const y0 = bandY0Accessor(scale, relAccessor)(datapoint);
    const y1 = bandY1Accessor(scale, relAccessor)(datapoint);
    return (y0 + y1) / 2;
  };
}
